import React from "react"
import Header from "../../components/new-components/Header/Header"
import FooterDetails from "../../components/new-components/Footer/FooterDetails/FooterDetails"
import SEO from "../../components/seo"

import {
  ArrowRight,
  Calendar,
  CheckCircle2,
  Heart,
  Star,
  UserCheck,
  UserCog,
  Users,
  PenTool,
} from "lucide-react"
import { Badge } from "../../components/static-components/badge"
import { Button } from "../../components/static-components/button"
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../../components/static-components/card"

import MainImage from "../../assets/roles/human resources.webp"
import OperationalView from "../../assets/products/pxp-integration-automation.webp"
import TailwindWrapper from "../../components/tailwind-wrapper"

const images = {
  header: MainImage,
  secondary: OperationalView,
}

export default function OperationsRolePage() {
  return (
    <TailwindWrapper>
      <Header />
      <div className="flex min-h-screen flex-col pt-7">
        <main className="flex-1">
          {/* Hero Section */}
          <section className="w-full py-12 md:py-24 lg:py-32 bg-gradient-to-b from-background to-muted">
            <div className="container px-4 md:px-6">
              <div className="grid gap-6 lg:grid-cols-[1fr_600px] lg:gap-12 xl:grid-cols-[1fr_750px]">
                <div className="flex flex-col justify-center space-y-4">
                  <div className="space-y-2">
                    <Badge className="inline-flex bg-primary text-primary-foreground">
                      Human Resources
                    </Badge>
                    <h1 className="text-3xl font-bold tracking-tighter sm:text-5xl xl:text-6xl/none font-sans">
                      Cultivate an Engaged Workforce for Outstanding Customer
                      Experiences
                    </h1>
                    <p className="max-w-[600px] text-muted-foreground md:text-xl">
                      Customer-centric organizations prioritize delivering
                      differentiated customer experiences, and engaged employees
                      are the key to building a truly customer-centric culture.
                      By empowering and engaging your workforce, you can reduce
                      turnover, improve overall job satisfaction, and drive
                      better business performance. Decades of data have
                      confirmed that engaged employees lead to lower turnover,
                      higher productivity, and happier customers.
                    </p>
                  </div>
                  <div className="flex flex-col gap-2 min-[400px]:flex-row">
                    <Button href="/book-demo" className="gap-1">
                      Book a demo <ArrowRight className="h-4 w-4" />
                    </Button>
                    <Button href="/solutions" variant="outline">
                      View solutions
                    </Button>
                  </div>
                </div>
                <div className="relative rounded-xl border bg-background p-2 shadow-lg">
                  <img
                    src={images.header}
                    alt="HR Dashboard"
                    className="rounded-lg object-cover w-full h-full"
                  />
                  <div className="absolute -bottom-4 -right-4 bg-primary text-primary-foreground px-4 py-2 rounded-lg text-sm font-medium shadow-lg">
                    Employee Experience
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Key Challenges Section */}
          <section className="w-full py-12 md:py-24 lg:py-32">
            <div className="container px-4 md:px-6">
              <div className="grid md:grid-cols-1 gap-12 items-center mx-[3vw] md:mx-[20vw]">
                <div className="space-y-4">
                  <Badge
                    variant="outline"
                    className="border-primary/20 text-primary"
                  >
                    The HR Challenge
                  </Badge>
                  <h2 className="text-3xl font-bold tracking-tighter md:text-4xl/tight">
                    You Can't Succeed Without Your Employees
                  </h2>
                  <p className="text-muted-foreground text-lg">
                    However, understanding your employees' sentiment and keeping
                    them engaged can be a significant challenge. Companies spend
                    billions each year managing workforce turnover, recruiting,
                    and training employees, especially deskless workers.
                    Research shows that in many cases, churn is primarily
                    related to avoidable factors, such as slow career
                    advancement, overwork, and lack of recognition from
                    management. How can you gain real-time visibility into your
                    employees' experiences?
                  </p>
                  <div className="bg-muted p-4 rounded-lg border border-border mt-6">
                    <p className="text-sm italic">
                      "Companies spend billions each year managing workforce
                      turnover, recruiting, and training employees, especially
                      deskless workers."
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* How Press'nXPress Can Help Section */}
          <section className="w-full py-12 md:py-24 lg:py-32 bg-muted">
            <div className="container px-4 md:px-6">
              <div className="flex flex-col items-center justify-center space-y-4 text-center">
                <div className="space-y-2">
                  <Badge
                    variant="outline"
                    className="border-primary/20 text-primary"
                  >
                    HR Excellence Enablers
                  </Badge>
                  <h2 className="text-3xl font-bold tracking-tighter md:text-4xl/tight">
                    How Press'nXPress Can Help
                  </h2>
                  <p className="max-w-[900px] text-muted-foreground md:text-xl/relaxed lg:text-base/relaxed xl:text-xl/relaxed">
                    Our platform provides the tools and capabilities HR teams
                    need to measure and improve the employee experience.
                  </p>
                </div>
              </div>
              <div className="mx-auto grid max-w-5xl items-center gap-6 py-12 md:grid-cols-2 lg:grid-cols-2">
                <Card className="bg-background/60 backdrop-blur">
                  <CardHeader>
                    <UserCheck className="h-10 w-10 text-primary mb-2" />
                    <CardTitle>
                      Reduce Employee Turnover and Improve Retention
                    </CardTitle>
                    <CardDescription>
                      You can't get the full picture in a vacuum. We help you
                      understand why employees leave or why some sites have
                      higher engagement, satisfaction, and retention than
                      others.
                    </CardDescription>
                  </CardHeader>
                </Card>
                <Card className="bg-background/60 backdrop-blur">
                  <CardHeader>
                    <PenTool className="h-10 w-10 text-primary mb-2" />
                    <CardTitle>
                      Identify Broken Processes, Inadequate Tools, and Training
                      Gaps
                    </CardTitle>
                    <CardDescription>
                      Uncover root causes of issues, from operational
                      inefficiencies to barriers, to gain a clear, real-time
                      picture of employee engagement at any given moment.
                    </CardDescription>
                  </CardHeader>
                </Card>
                <Card className="bg-background/60 backdrop-blur">
                  <CardHeader>
                    <UserCog className="h-10 w-10 text-primary mb-2" />
                    <CardTitle>Measure Team Effectiveness</CardTitle>
                    <CardDescription>
                      Empower your company's teams of all types to do their best
                      work together by measuring and improving team dynamics and
                      performance.
                    </CardDescription>
                  </CardHeader>
                </Card>
                <Card className="bg-background/60 backdrop-blur">
                  <CardHeader>
                    <Calendar className="h-10 w-10 text-primary mb-2" />
                    <CardTitle>Measure the Moments that Matter</CardTitle>
                    <CardDescription>
                      Provide full lifecycle coverage across the employee
                      experience in your organization. Capture feedback from the
                      day your people join to the day they leave.
                    </CardDescription>
                  </CardHeader>
                </Card>
              </div>
            </div>
          </section>

          {/* Continuous Feedback Section */}
          <section className="w-full py-12 md:py-24 lg:py-32">
            <div className="container px-4 md:px-6">
              <div className="grid md:grid-cols-1 gap-12 items-center mx-[3vw] md:mx-[20vw]">
                <div>
                  <Badge
                    variant="outline"
                    className="border-primary/20 text-primary mb-4"
                  >
                    Continuous Feedback and Action
                  </Badge>
                  <h2 className="text-3xl font-bold tracking-tighter md:text-4xl/tight mb-4">
                    Go Beyond Annual HR Surveys
                  </h2>
                  <p className="text-muted-foreground mb-8">
                    Go beyond annual or biannual HR surveys by collecting,
                    analyzing, and acting on employee feedback on an ongoing
                    basis.
                  </p>

                  <ul className="space-y-4">
                    <li className="flex items-start gap-3">
                      <CheckCircle2 className="h-5 w-5 text-secondary mt-1" />
                      <div>
                        <h4 className="font-medium">
                          Proactive Issue Identification
                        </h4>
                        <p className="text-sm text-muted-foreground">
                          Proactively identify and address key drivers of
                          disengagement, reducing unwanted and costly attrition.
                        </p>
                      </div>
                    </li>
                    <li className="flex items-start gap-3">
                      <CheckCircle2 className="h-5 w-5 text-secondary mt-1" />
                      <div>
                        <h4 className="font-medium">Impact Measurement</h4>
                        <p className="text-sm text-muted-foreground">
                          Understand how your initiatives impact employee
                          productivity and organizational KPIs.
                        </p>
                      </div>
                    </li>
                    <li className="flex items-start gap-3">
                      <CheckCircle2 className="h-5 w-5 text-secondary mt-1" />
                      <div>
                        <h4 className="font-medium">Manager Empowerment</h4>
                        <p className="text-sm text-muted-foreground">
                          Empower managers with clear, actionable insights to
                          drive improvements within their teams.
                        </p>
                      </div>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </section>

          {/* Employee Engagement Section */}
          <section className="w-full py-12 md:py-24 lg:py-32 bg-muted">
            <div className="container px-4 md:px-6">
              <div className="flex flex-col items-center justify-center space-y-4 text-center mb-12">
                <div className="space-y-2">
                  <Badge
                    variant="outline"
                    className="border-primary/20 text-primary"
                  >
                    Employee Engagement
                  </Badge>
                  <h2 className="text-3xl font-bold tracking-tighter md:text-4xl/tight">
                    Engaged Employees Create Delighted Customers
                  </h2>
                  <p className="max-w-[900px] text-muted-foreground md:text-xl/relaxed lg:text-base/relaxed xl:text-xl/relaxed">
                    Discover how employee engagement directly impacts customer
                    satisfaction and business outcomes.
                  </p>
                </div>
              </div>

              <div className="grid gap-6 lg:grid-cols-3">
                <Card className="bg-background/60 backdrop-blur">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Users className="h-6 w-6 text-primary" />
                      Employee Satisfaction
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p>
                      Measure and track employee satisfaction across
                      departments, locations, and teams to identify areas of
                      strength and opportunities for improvement.
                    </p>
                  </CardContent>
                </Card>

                <Card className="bg-background/60 backdrop-blur">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Heart className="h-6 w-6 text-primary" />
                      Customer Connection
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p>
                      Understand the direct correlation between employee
                      engagement and customer satisfaction metrics, enabling
                      targeted improvements.
                    </p>
                  </CardContent>
                </Card>

                <Card className="bg-background/60 backdrop-blur">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Star className="h-6 w-6 text-primary" />
                      Recognition Programs
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p>
                      Develop and measure the effectiveness of employee
                      recognition programs that reinforce customer-centric
                      behaviors and drive business results.
                    </p>
                  </CardContent>
                </Card>
              </div>
            </div>
          </section>

          {/* Business Impact Section */}
          <section className="w-full py-12 md:py-24 lg:py-32 bg-gradient-to-b from-background to-secondary/10">
            <div className="container px-4 md:px-6">
              <div className="grid md:grid-cols-2 gap-12 items-center">
                <div className="space-y-4">
                  <Badge
                    variant="outline"
                    className="border-primary/20 text-primary"
                  >
                    Business Impact
                  </Badge>
                  <h2 className="text-3xl font-bold tracking-tighter md:text-4xl/tight">
                    Connect Employee Experience to Business Outcomes
                  </h2>
                  <p className="text-muted-foreground text-lg">
                    Demonstrate the impact of employee experience on customer
                    satisfaction, retention, and business results.
                  </p>
                  <ul className="space-y-4 mt-6">
                    <li className="flex items-start gap-3">
                      <CheckCircle2 className="h-5 w-5 text-primary mt-1" />
                      <div>
                        <h4 className="font-medium">
                          Increased Employee Engagement
                        </h4>
                        <p className="text-muted-foreground">
                          Build a more engaged workforce that is committed to
                          delivering exceptional customer experiences.
                        </p>
                      </div>
                    </li>
                    <li className="flex items-start gap-3">
                      <CheckCircle2 className="h-5 w-5 text-primary mt-1" />
                      <div>
                        <h4 className="font-medium">Reduced Turnover</h4>
                        <p className="text-muted-foreground">
                          Identify and address drivers of employee turnover to
                          improve retention and reduce costs.
                        </p>
                      </div>
                    </li>
                    <li className="flex items-start gap-3">
                      <CheckCircle2 className="h-5 w-5 text-primary mt-1" />
                      <div>
                        <h4 className="font-medium">
                          Improved Customer Satisfaction
                        </h4>
                        <p className="text-muted-foreground">
                          Demonstrate the connection between employee engagement
                          and customer satisfaction metrics.
                        </p>
                      </div>
                    </li>
                    <li className="flex items-start gap-3">
                      <CheckCircle2 className="h-5 w-5 text-primary mt-1" />
                      <div>
                        <h4 className="font-medium">
                          Higher Business Performance
                        </h4>
                        <p className="text-muted-foreground">
                          Show how improvements in employee experience translate
                          to better business results.
                        </p>
                      </div>
                    </li>
                  </ul>
                </div>
                <div className="relative rounded-xl border bg-background p-2 shadow-xl">
                  <img
                    src={images.secondary}
                    alt="Business Impact Dashboard"
                    className="rounded-lg object-cover w-full h-full"
                  />
                </div>
              </div>
            </div>
          </section>

          {/* CTA Section */}
          <section className="w-full py-12 md:py-24 lg:py-32 bg-primary text-primary-foreground">
            <div className="container px-4 md:px-6">
              <div className="flex flex-col items-center justify-center space-y-4 text-center">
                <div className="space-y-2">
                  <h2 className="text-3xl font-bold tracking-tighter md:text-4xl/tight">
                    Ready to transform your employee experience?
                  </h2>
                  <p className="max-w-[600px] mx-auto text-center md:text-xl/relaxed lg:text-base/relaxed xl:text-xl/relaxed">
                    Keep employees engaged and create delightful experiences for
                    your customers.
                  </p>
                </div>
                <div className="flex flex-col gap-2 min-[400px]:flex-row">
                  <Button
                    variant="secondary"
                    className="gap-1"
                    href="/book-demo"
                  >
                    Book a demo <ArrowRight className="h-4 w-4" />
                  </Button>
                  <Button
                    href="/products/dashboard"
                    variant="outline"
                    className="bg-transparent text-primary-foreground border-primary-foreground hover:bg-primary-foreground/10"
                  >
                    Learn more
                  </Button>
                </div>
                <p className="text-sm opacity-80 pt-4">
                  See how our platform can help you measure and improve the
                  employee experience to drive better business outcomes.
                </p>
              </div>
            </div>
          </section>
        </main>
      </div>
      <FooterDetails
        name="Operations"
        section="Roles"
        slug="/roles/operations"
      />
    </TailwindWrapper>
  )
}

export const Head = () => (
  <SEO
    title="Human Resources"
    description="Cultivate an Engaged Workforce for Outstanding Customer Experiences"
  />
)
